import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { archiveBranch, createBranch, getBranchDetail, listCompanyOptions, updateBranch } from '../api/branchApi'
import { submitMasterChange } from '../../../approval/masters/api/masterApprovalApi'
import { appToast } from '../../../../components/AppToast'

function normalizeOptionsToItems(data) {
  const arr = Array.isArray(data) ? data : []
  return arr.map((option) => ({ value: String(option.id), label: option.name }))
}

function normalizeText(value) {
  const trimmed = String(value || '').trim()
  return trimmed.length > 0 ? trimmed : undefined
}

export function useBranchEntryScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editingBranchId = searchParams.get('id')

  const savingRef = useRef(false)

  const [form, setForm] = useState({
    company_id: '',
    name: '',
    gst_number: '',
    branch_type: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    contact_person: '',
    contact_number: '',
    email: '',
  })
  const [operationalStatus, setOperationalStatus] = useState('active')
  const [initialLabels, setInitialLabels] = useState({
    company_name: '',
  })

  const [isLoading, setIsLoading] = useState(Boolean(editingBranchId))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pendingApproval, setPendingApproval] = useState(null)

  // ── Dynamic fetchOptions callback for AppDropdown ─────────────────────────
  const fetchCompanyOptions = useCallback(async (search, page, pageSize) => {
    const data = await listCompanyOptions({ search, page, page_size: pageSize })
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  useEffect(() => {
    let active = true

    async function loadIfEditing() {
      if (!editingBranchId) return
      setIsLoading(true)
      setError('')

      try {
        const detail = await getBranchDetail(editingBranchId)
        if (!active) return

        setForm({
          company_id: detail?.company_id ? String(detail.company_id) : '',
          name: detail?.name || '',
          gst_number: detail?.gst_number || '',
          branch_type: detail?.branch_type || '',
          address: detail?.address || '',
          city: detail?.city || '',
          state: detail?.state || '',
          country: detail?.country || '',
          pincode: detail?.pincode || '',
          contact_person: detail?.contact_person || '',
          contact_number: detail?.contact_number || '',
          email: detail?.email || '',
        })
        setInitialLabels({
          company_name: detail?.company_name || '',
        })

        setOperationalStatus(Number(detail?.is_archive) === 1 ? 'inactive' : 'active')
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load branch details.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadIfEditing()

    return () => {
      active = false
    }
  }, [editingBranchId])

  const completionPercent = useMemo(() => {
    const trackedFields = ['name', 'company_id', 'address', 'state', 'email', 'contact_number', 'city', 'country', 'pincode']
    const filledCount = trackedFields.filter((key) => String(form[key] || '').trim().length > 0).length
    return Math.round((filledCount / trackedFields.length) * 100)
  }, [form])

  const completionStepText = useMemo(() => {
    if (completionPercent >= 67) return 'Step 3 of 3'
    if (completionPercent >= 34) return 'Step 2 of 3'
    return 'Step 1 of 3'
  }, [completionPercent])

  function patchForm(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function buildPayload() {
    return {
      company_id: Number(form.company_id),
      name: normalizeText(form.name),
      gst_number: normalizeText(form.gst_number),
      branch_type: normalizeText(form.branch_type),
      address: normalizeText(form.address),
      city: normalizeText(form.city),
      state: normalizeText(form.state),
      country: normalizeText(form.country),
      pincode: normalizeText(form.pincode),
      contact_person: normalizeText(form.contact_person),
      contact_number: normalizeText(form.contact_number),
      email: normalizeText(form.email),
      is_archive: operationalStatus === 'inactive' ? 1 : 0,
    }
  }

  async function handleSave() {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')

    if (!String(form.name || '').trim()) {
      setError('Branch name is required.')
      savingRef.current = false
      return
    }

    if (!String(form.company_id || '').trim()) {
      setError('Parent company is required.')
      savingRef.current = false
      return
    }

    if (!String(form.state || '').trim()) {
      setError('State is required.')
      savingRef.current = false
      return
    }

    setIsSaving(true)

    try {
      const payload = buildPayload()
      const action = editingBranchId ? 'UPDATE' : 'CREATE'

      const result = await submitMasterChange({
        module_key: 'branch',
        action,
        entity_id: editingBranchId ? Number(editingBranchId) : undefined,
        payload,
      })

      const msg = result?.message || (editingBranchId
        ? 'Branch update submitted for approval.'
        : 'Branch creation submitted for approval.')
      appToast.success(msg)
      setPendingApproval({
        requestId: result?.approval_request_id,
        message: msg,
      })
      setSuccess(msg)
      savingRef.current = false
      setIsSaving(false)
      navigate('/masters/branch/list')
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to submit branch change for approval.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  return {
    state: {
      form,
      operationalStatus,
      completionPercent,
      completionStepText,
      isLoading,
      isSaving,
      error,
      success,
      pendingApproval,
      isEditMode: Boolean(editingBranchId),
      initialLabels,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
      fetchCompanyOptions,
    },
  }
}
